import React, { useContext, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FcGoogle } from "react-icons/fc";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebaseConfig";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContainer = styled.div`
  background: rgba(28, 28, 28, 0.95);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 20px;
  padding: 32px;
  width: 420px;
  max-width: 90%;
  color: #e0e0e0;
  position: relative;
  box-shadow: 0 0 25px rgba(147, 51, 234, 0.4);
  animation: ${fadeIn} 0.35s ease;
  @media (max-width: 480px) {
    padding: 24px 20px;
    border-radius: 16px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 16px;
  border: none;
  background: transparent;
  color: #bbb;
  font-size: 24px;
  cursor: pointer;
  transition: 0.2s ease;
  &:hover {
    color: #fff;
  }
`;

const Tabs = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 24px;
  border-bottom: 1px solid #333;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 0;
  background: transparent;
  border: none;
  font-size: 16px;
  font-weight: 600;
  color: ${({ active }) => (active ? "#a855f7" : "#aaa")};
  border-bottom: ${({ active }) => (active ? "3px solid #a855f7" : "none")};
  transition: all 0.3s ease;
  &:hover {
    color: #9333ea;
  }
`;

const Input = styled(Field)`
  width: 100%;
  padding: 14px 16px;
  margin-bottom: 10px;
  border-radius: 10px;
  border: 1px solid #444;
  background: #2a2a2a;
  color: #fff;
  font-size: 15px;
  transition: all 0.25s ease;
  &:focus {
    border-color: #a855f7;
    box-shadow: 0 0 5px rgba(168, 85, 247, 0.6);
    outline: none;
  }
  &::placeholder {
    color: #888;
  }
`;

const ErrorText = styled.div`
  color: #f87171;
  font-size: 13px;
  margin-bottom: 10px;
`;

const ForgotPassword = styled.p`
  font-size: 14px;
  color: #a855f7;
  text-align: right;
  margin: -5px 0 18px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 0;
  border: none;
  border-radius: 12px;
  background: linear-gradient(90deg, #9333ea, #a855f7);
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 0 rgba(168, 85, 247, 0);
  &:hover {
    background: linear-gradient(90deg, #a855f7, #9333ea);
    box-shadow: 0 0 20px rgba(168, 85, 247, 0.5);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 18px 0;
  hr {
    flex: 1;
    height: 1px;
    background: #333;
    border: none;
  }
  span {
    margin: 0 10px;
    font-size: 14px;
    color: #aaa;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 12px 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 12px rgba(255, 255, 255, 0.15);
  }
`;

const Loader = styled.div`
  border: 5px solid #eee;
  border-top: 5px solid #a855f7;
  border-radius: 50%;
  width: 55px;
  height: 55px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(10, 10, 10, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  z-index: 100;
`;

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().min(6, "Too short").required("Password is required"),
});

const SignUpSchema = Yup.object().shape({
  fullName: Yup.string().min(3, "At least 3 characters").required(),
  email: Yup.string().email("Invalid email").required(),
  password: Yup.string().min(6, "At least 6 chars").required(),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required(),
});

export default function AuthModal({ isOpen, onClose }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const getAuthErrorMessage = (code) => {
    const errors = {
      "auth/user-not-found": "No user found with this email.",
      "auth/wrong-password": "Incorrect password.",
      "auth/invalid-email": "Invalid email format.",
      "auth/email-already-in-use": "Email already in use.",
      "auth/weak-password": "Password too weak.",
    };
    return errors[code] || "Something went wrong. Try again.";
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      const googleProvider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          id: user.uid,
          name: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
          createdAt: new Date(),
        });
      }

      setUser({
        id: user.uid,
        name: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
      });

      toast.success("Signed in with Google!");
      onClose();
    } catch (error) {
      toast.error("Google Sign-In failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Overlay>
      <ModalContainer>
        {loading && (
          <LoadingOverlay>
            <Loader />
          </LoadingOverlay>
        )}
        <CloseButton onClick={onClose}>×</CloseButton>

        <Tabs>
          <Tab active={isLogin} onClick={() => setIsLogin(true)}>
            Login
          </Tab>
          <Tab active={!isLogin} onClick={() => setIsLogin(false)}>
            Sign Up
          </Tab>
        </Tabs>

        <Formik
          initialValues={
            isLogin
              ? { email: "", password: "" }
              : { fullName: "", email: "", password: "", confirmPassword: "" }
          }
          validationSchema={isLogin ? LoginSchema : SignUpSchema}
          onSubmit={async (values) => {
            setLoading(true);
            try {
              if (isLogin) {
                const userCred = await signInWithEmailAndPassword(
                  auth,
                  values.email,
                  values.password
                );
                const user = userCred.user;
                const userDoc = await getDoc(doc(db, "users", user.uid));
                setUser(
                  userDoc.exists()
                    ? userDoc.data()
                    : { id: user.uid, email: user.email }
                );
                toast.success("Welcome back!");
              } else {
                const userCred = await createUserWithEmailAndPassword(
                  auth,
                  values.email,
                  values.password
                );
                await updateProfile(userCred.user, {
                  displayName: values.fullName,
                });
                const newUser = {
                  id: userCred.user.uid,
                  name: values.fullName,
                  email: values.email,
                };
                await setDoc(doc(db, "users", newUser.id), newUser);
                setUser(newUser);
                toast.success("Account created successfully!");
              }
              onClose();
            } catch (e) {
              toast.error(getAuthErrorMessage(e.code));
            }
            setLoading(false);
          }}
        >
          {() => (
            <Form>
              {!isLogin && (
                <>
                  <Input name="fullName" placeholder="Full Name" />
                  <ErrorMessage name="fullName" component={ErrorText} />
                </>
              )}
              <Input name="email" placeholder="Email" type="email" />
              <ErrorMessage name="email" component={ErrorText} />
              <Input name="password" placeholder="Password" type="password" />
              <ErrorMessage name="password" component={ErrorText} />

              {isLogin && (
                <ForgotPassword onClick={() => navigate("/forgot-password")}>
                  Forgot password?
                </ForgotPassword>
              )}

              {!isLogin && (
                <>
                  <Input
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    type="password"
                  />
                  <ErrorMessage name="confirmPassword" component={ErrorText} />
                </>
              )}

              <SubmitButton type="submit">
                {isLogin ? "Login" : "Sign Up"}
              </SubmitButton>
            </Form>
          )}
        </Formik>

        <Divider>
          <hr />
          <span>OR</span>
          <hr />
        </Divider>

        <GoogleButton onClick={handleGoogleLogin}>
          <FcGoogle size={22} />{" "}
          {isLogin ? "Continue with Google" : "Sign up with Google"}
        </GoogleButton>
      </ModalContainer>
    </Overlay>
  );
}
